import { useMemo } from 'react'
import { PROCUREMENT_MATRICES, ITEM_STATUS_LABELS_RU, PROCUREMENT_STATE_LABELS } from '@crm/shared'
import { PRIORITY_LABELS } from '../components/ItemsTable'
import { customsRowFields } from '../components/itemColDefs.customs'
import type { Request, RequestItem, RequestItemStatus } from '../../../shared/api/types'

type ProcUser = { id: string }

export function useVisibleItems(
  request: Request | undefined,
  isProcurement: boolean,
  itemSearch: string,
  itemStatusFilter: RequestItemStatus | '',
  procUsers: ProcUser[],
  notesMap: Map<string, Map<string, string>>,
  /** @deprecated оставлено в сигнатуре для обратной совместимости вызовов. */
  userRole?: string,
) {
  void userRole // сортировка по роли убрана — позиция не уезжает вниз после создания квоты
  const filtered: RequestItem[] = (request?.items ?? []).filter((item) => {
    if (isProcurement && !PROCUREMENT_MATRICES.includes(item.matrix as never)) return false
    if (itemStatusFilter && item.status !== itemStatusFilter) return false
    if (itemSearch) {
      const q = itemSearch.toLowerCase()
      return (
        item.partNumber.toLowerCase().includes(q) || (item.brand ?? '').toLowerCase().includes(q)
      )
    }
    return true
  })

  // Порядок позиций — только из API (orderBy: priority asc, createdAt asc).
  // Клиентских перестановок нет: позиция не должна уезжать вниз, когда
  // менеджер/закупщик создаёт квоту (раньше MANAGER/ROS получали сорт
  // «сначала без квот», коммит 6155e19 откачен по просьбе Сергея 21.04).
  const visibleItems: RequestItem[] = filtered

  const agRowData = useMemo<Record<string, unknown>[]>(() => {
    return visibleItems.map((item) => {
      const row: Record<string, unknown> = {
        id: item.id,
        partNumber: item.partNumber,
        matrix: item.matrix ?? '',
        noteForProcurement: item.noteForProcurement ?? null,
        brand: item.brand ?? null,
        category: item.category ?? null,
        quantity: item.quantity,
        unit: item.unit ?? 'pcs',
        targetPrice: item.targetPrice ?? null,
        priceStock: item.priceStock ?? null,
        priceManufacturer: item.priceManufacturer ?? null,
        priceRF: item.priceRF,
        priceOther: item.priceOther ?? null,
        comment: item.comment ?? '',
        kpNote: item.kpNote ?? '',
        priority: item.priority,
        priorityLabel: PRIORITY_LABELS[item.priority] ?? item.priority,
        status: item.status,
        statusLabel: ITEM_STATUS_LABELS_RU[item.status] ?? item.status,
        assignedToEmail: item.assignedTo?.email ?? '—',
        assignedToId: item.assignedTo?.id ?? null,
        results: item.results ?? [],
        resultsCount: item._count?.results ?? 0,
        commentsCount: item._count?.comments ?? 0,
        procurementState: item.procurementState ?? undefined,
        procurementStateLabel: item.procurementState
          ? (PROCUREMENT_STATE_LABELS[item.procurementState] ?? item.procurementState)
          : '—',
        teamProcurementState: item.teamProcurementState ?? undefined,
        teamProcurementStateLabel: item.teamProcurementState
          ? (PROCUREMENT_STATE_LABELS[item.teamProcurementState] ?? item.teamProcurementState)
          : '—',
        // Даташит и нетарифка (итог проверки логиста) + плоские подписи для фильтров.
        ...customsRowFields(item, request?.status),
      }
      for (const pu of procUsers) {
        row[`proc_${pu.id}`] = notesMap.get(item.id)?.get(pu.id) ?? ''
      }
      return row
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- visibleItems пересчитывается синхронно
  }, [request?.items, isProcurement, itemSearch, itemStatusFilter, procUsers, notesMap, userRole])

  return { visibleItems, agRowData }
}
